import {useEffect} from 'react';
import {type UseFormSetValue} from 'react-hook-form';
import {SelectOption, DocumentChoice, GUEST_FORM_NAMES} from '@chekin/core';
import {useIVStore} from '../../../store/useIVStore';
import {getDocumentType} from '../../../store/selectors/getDocumentType';
import {getDocumentNationality} from '../../../store/selectors/getDocumentNationality';
import {useFetchLocations} from '../../../hooks/useFetchLocations';
import {DocumentSelectionFormTypes} from './types';

type InitDocumentTypeProps = {
  nationality?: SelectOption;
  value?: SelectOption<DocumentChoice> | null;
  options: SelectOption<DocumentChoice>[];
  setValue: UseFormSetValue<DocumentSelectionFormTypes>;
};
export const useInitDocumentType = ({
  options,
  setValue,
  nationality,
  value,
}: InitDocumentTypeProps) => {
  const storedDocumentType = useIVStore(getDocumentType);

  useEffect(() => {
    if (!nationality || !options.length) return;

    const isCurrentSelectionValid = options.some(option => option.value === value?.value);
    if (isCurrentSelectionValid) return;

    const fallback =
      options.find(option => option.value === storedDocumentType?.value) ?? options[0];

    if (fallback) {
      setValue(GUEST_FORM_NAMES.document_type, fallback);
    }
  }, [nationality, options, setValue, storedDocumentType, value]);
};

type InitNationalityProps = {
  setValue: UseFormSetValue<DocumentSelectionFormTypes>;
};
export const useInitNationality = ({setValue}: InitNationalityProps) => {
  const {isLoadingLocations, locationsAsOptions} = useFetchLocations();
  const nationality = useIVStore(getDocumentNationality);

  useEffect(() => {
    if (!isLoadingLocations) {
      const locationOption = locationsAsOptions.find(
        location => location.value === nationality,
      );

      if (locationOption) {
        setValue(GUEST_FORM_NAMES.nationality, locationOption);
      }
    }
  }, [isLoadingLocations, locationsAsOptions, nationality, setValue]);
};
